import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import Immerable from '@naruto/naruto-models/src/types/GeneralImmer';

class Utility extends Immerable {
  /**
   * 名稱 : delay
   * 功能 : 延遲執行
   */
  delay = (ms: number): Promise<number> => {
    const range = 30; // JS settimeout時間稍微不準, 故多給一些範圍, 有些許容錯空間
    return new Promise(resolve => {
      setTimeout(resolve, ms + range);
    });
  };

  /**
   * 名稱 : getIsEmail
   * 功能 : 確認是否為合法的 email
   */
  getIsEmail = (email: string): boolean => {
    const regexEmail =
      // eslint-disable-next-line no-useless-escape
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regexEmail.test(email);
  };

  /**
   * 名稱 : checkIsOverdue
   * 功能 : 查詢指定日期字串是否過期
   */
  checkIsOverdue = (expectDate: string, actualDate?: string | string[]): boolean => {
    if (actualDate) {
      if (!Array.isArray(actualDate)) {
        return dayjs(actualDate).startOf('d').isAfter(dayjs(expectDate).startOf('d'));
      }
      if (Array.isArray(actualDate)) {
        if (actualDate.length > 0) {
          const sortDate = cloneDeep(actualDate).sort((dateStringA, dateStringB) => {
            const dateA = dayjs(dateStringA);
            const dateB = dayjs(dateStringB);
            if (dateA.isAfter(dateB)) {
              return 1;
            }
            if (dateA.isBefore(dateB)) {
              return -1;
            }
            return 0;
          });
          return dayjs(sortDate[0]).startOf('d').isAfter(dayjs(expectDate).startOf('d'));
        }
      }
    }
    // 同一天不算逾期
    return dayjs().add(-1, 'd').isAfter(dayjs(expectDate));
  };

  /**
   * 名稱 : isObjectID
   * 功能 : 查詢是否為 object id
   */
  isObjectID = (str: string): boolean => /^[0-9a-fA-F]{24}$/.test(str);

  /**
   * 名稱 : parseBool
   * 功能 : 解析 boolean
   */
  parseBool = (v?: string, defaultValue?: string): boolean => {
    switch (v) {
      case 'true':
      case '1':
      case 'yes':
      case 'y':
        return true;
      case 'false':
      case '0':
      case 'no':
      case 'n':
        return false;
      default:
        return defaultValue === undefined ? false : !!defaultValue;
    }
  };

  /**
   * 名稱 : updateNestedValue
   * 功能 : 更新指定 key 的值
   */
  updateNestedValue = (obj: any, targetKey: string, newValue: any): void => {
    Object.keys(obj).forEach(key => {
      if (key === targetKey) {
        obj[key] = newValue;
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        this.updateNestedValue(obj[key], targetKey, newValue);
      } else if (obj[targetKey] === undefined) {
        obj[targetKey] = newValue;
      }
    });
  };

  /**
   * 名稱 : getValidBackUrl
   * 功能 : 取得合法上一頁的路徑
   */
  getValidBackUrl = (validUrls: string[], checkedUrls: string[]): string | undefined => {
    const target = checkedUrls.find(url => validUrls.find(validUrl => url.includes(validUrl)));
    if (target) {
      return target;
    }
    return undefined;
  };

  /**
   * 名稱 : generateKey
   * 功能 : 產生加密金鑰
   */
  generateKey = async (password: string, salt = 'naruto_tools'): Promise<CryptoKey> => {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey('raw', encoder.encode(password), { name: 'PBKDF2' }, false, [
      'deriveKey'
    ]);

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode(salt), // 固定的 Salt（可以改為隨機存儲）
        iterations: 100000, // 迭代次數
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 }, // ✅ 確保 AES 金鑰長度為 256 bits
      true,
      ['encrypt', 'decrypt']
    );
  };

  /**
   * 名稱 : getDuplicateStrings
   * 功能 : 取得重複的字串
   */
  getDuplicateObjects<T>(arr: T[]): T[] {
    const seen = new Map<string, { obj: T; count: number }>();
    const duplicates: T[] = [];

    for (const obj of arr) {
      const key = JSON.stringify(obj); // 用 JSON 序列化來判斷完全相同的物件
      const entry = seen.get(key);

      if (entry) {
        entry.count += 1;
        if (entry.count === 2) {
          duplicates.push(obj); // 只在第二次遇到時加入 duplicates
        }
      } else {
        seen.set(key, { obj, count: 1 });
      }
    }

    return duplicates;
  }

  /**
   * 名稱 : sanitizeNumericInput
   * 功能 : 限制浮點類型字串輸入翻為
   */
  sanitizeNumericInput = (value: string, max = 10, min = 0): string => {
    // 1️⃣ 僅允許數字與一個小數點
    value = value.replace(/[^0-9.]/g, '');
    const dotCount = (value.match(/\./g) || []).length;
    if (dotCount > 1) {
      const firstDotIndex = value.indexOf('.');
      value = value.slice(0, firstDotIndex + 1) + value.slice(firstDotIndex + 1).replace(/\./g, '');
    }

    // 2️⃣ 如果最後一個字是 "."（例如 "3."），暫時允許
    if (value.endsWith('.')) {
      const beforeDot = value.slice(0, -1);
      const num = parseFloat(beforeDot);
      if (Number.isNaN(num)) return value;
      if (num > max) return String(max);
      return value;
    }

    // 3️⃣ 去除前導 0，但保留 0.x 與 x.0x
    if (
      value !== '' &&
      value !== '0' &&
      !value.startsWith('0.') &&
      !value.includes('.') // ⬅️ 新增：有小數時不轉成 Number()
    ) {
      value = String(Number(value));
    }

    // 4️⃣ 限制範圍在 min～max
    const numericValue = parseFloat(value);
    if (!Number.isNaN(numericValue)) {
      if (numericValue < min) value = String(min);
      if (numericValue > max) value = String(max);
    }

    return value;
  };
}

export default Utility;
